import type { FC } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';
import Box from '@mui/material/Box';
import { TextField } from 'components/common';
import { Button, Divider, IconButton, Typography } from '@mui/material';
import { AddOutlined, DeleteOutline, TranslateOutlined } from '@mui/icons-material';
import { FormControlContainer } from './CreativeTextsComponents';
import scrollIntoView from 'utils/scrollIntoView';
import { AnimatedContainer, LocalizationsVersionContainer } from './AddCreativeTextLocalizations.styles';
import SelectLanguage from './SelectLanguage';

interface AddCreativeTextLocalizationsProps {
  autoAddOnMount?: boolean;
}

const AddCreativeTextLocalizations: FC<AddCreativeTextLocalizationsProps> = ({
  autoAddOnMount = false,
}) => {
  const {
    control,
  } = useFormContext();
  const { fields, append, remove } = useFieldArray({
    control,
    name: 'localizations',
  });
  const [removingIndex, setRemovingIndex] = useState<number | null>(null);
  const hasAutoAddedRef = useRef(false);

  const handleAddLocalization = useCallback(() => {
    append({
      title: '',
      body: '',
      language: '',
      description: '',
    });
    scrollIntoView(`[id*="localizations-"]`);
  }, [append]);

  useEffect(() => {
    if (!autoAddOnMount || hasAutoAddedRef.current) {
      return;
    }

    hasAutoAddedRef.current = true;
    handleAddLocalization();
  }, [autoAddOnMount, handleAddLocalization]);

  const handleRemoveLocalization = useCallback(
    (index: number) => {
      setRemovingIndex(index);
      setTimeout(() => {
        remove(index);
        setRemovingIndex(null);
      }, 500);
    },
    [remove],
  );

  return (
    <Box
      sx={{
        display: 'flex',
        width: '100%',
        flexDirection: 'column',
      }}
    >
      {fields.map((field, index) => {
        const isRemoving = removingIndex === index;

        return (
          <AnimatedContainer
            key={`localizations-${index}`}
            id={`localizations-${index}`}
            className={isRemoving ? 'removing' : ''}
          >
            <Divider
              orientation="horizontal"
              variant="middle"
              flexItem
              sx={{ my: 0, mx: 0 }}
            />
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                width: '100%',
                alignItems: 'center',
                gap: 1,
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  width: '100%',
                  alignItems: 'center',
                  gap: 1,
                }}
              >
                <TranslateOutlined color={'primary'} />
                <Typography variant={'subtitle1'}>
                  Localized version #{index + 1}
                </Typography>
              </Box>
              <IconButton
                onClick={() => {
                  handleRemoveLocalization(index);
                }}
                disabled={isRemoving}
              >
                <DeleteOutline />
              </IconButton>
            </Box>
            <LocalizationsVersionContainer key={field.id}>
              <FormControlContainer title="Title">
                <TextField
                  name={`localizations.${index}.title`}
                  placeholder={'Value'}
                />
              </FormControlContainer>
              <FormControlContainer title="Body">
                <TextField
                  name={`localizations.${index}.body`}
                  placeholder={'Value'}
                  multiline
                />
              </FormControlContainer>
              <FormControlContainer title="Details">
                <SelectLanguage name={`localizations.${index}.language`} />
              </FormControlContainer>
              <FormControlContainer title="Description (optional)">
                <TextField
                  name={`localizations.${index}.description`}
                  placeholder={'Value'}
                  multiline
                  inputProps={{
                    sx: {
                      '&.MuiInputBase-input': {
                        minHeight: '150px',
                        borderRadius: 0,
                      },
                    },
                  }}
                />
              </FormControlContainer>
            </LocalizationsVersionContainer>
          </AnimatedContainer>
        );
      })}
      <Box>
        <Button
          onClick={handleAddLocalization}
          startIcon={<AddOutlined />}
          variant={'text'}
        >
          Add localized version
        </Button>
      </Box>
    </Box>
  );
};

export default AddCreativeTextLocalizations;
